import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiStar, FiExternalLink, FiSmartphone } from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

export default function ReviewPage() {
  const tidycalLink = "https://tidycal.com/drivepal/review?utm_source=tidycal&utm_medium=review_link&utm_campaign=share";
  const googleLink = "https://g.page/r/CQ6M_OJwlp4JEAI/review";
  const qrCodeImg = "https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/images/aW1hZ2UvcG5n/26847bc5a3ee7f918757f02a6c74be84.png";
  const reviewImages = [
    "https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/images/aW1hZ2UvanBlZw==/b88c8f24af8b39e4976eff84af16f711.jpeg",
    "https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/images/aW1hZ2UvanBlZw==/407c63d7dd50b69f69d61bb1c9b53f19.jpeg",
    "https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/images/aW1hZ2UvanBlZw==/a62a85d5d8e3490e64fef78091e293f2.jpeg",
    "https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/images/aW1hZ2UvanBlZw==/734e9c18aa899cd6eb99cfa8ad7c5731.jpeg"
  ];

  useEffect(() => {
    document.title = "Leave a Review | Drive Pal Driving School Goulburn";
    const setMeta = (name, content, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Leave a review for Drive Pal Driving School in Goulburn NSW. Share your experience with instructor Santosh and help other learner drivers find quality automatic driving lessons.');
    setMeta('og:title', 'Leave a Review | Drive Pal Driving School Goulburn NSW', true);
    setMeta('og:description', 'Share your driving lesson experience with Drive Pal Driving School in Goulburn NSW.', true);
    setMeta('og:url', 'https://drivepal.au/#/review', true);
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-brand-offwhite">
      <section className="bg-brand-navy py-16 px-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gold/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="container mx-auto text-center relative z-10 max-w-3xl">
          <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} className="w-16 h-16 bg-brand-gold rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <SafeIcon icon={FiStar} className="text-brand-navy text-3xl" />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl md:text-5xl text-white mb-4">
            How Did We <span className="text-brand-gold">Do?</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white/80 text-lg md:text-xl font-medium">
            Your feedback helps us improve and helps other students find a driving instructor they can trust.
          </motion.p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl shadow-xl border-t-8 border-[#4285F4] p-8 flex flex-col items-center text-center relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <SafeIcon icon={FaGoogle} className="text-6xl text-[#4285F4]" />
              </div>
              <h2 className="font-display text-2xl text-brand-navy mb-2 relative z-10">Review on Google</h2>
              <p className="text-gray-600 mb-8 relative z-10">Scan the QR code below with your phone camera, or click the button to leave a review on our Google Business profile.</p>
              <div className="bg-white p-4 rounded-xl shadow-md border-2 border-gray-100 mb-8 relative z-10 group-hover:scale-105 transition-transform">
                <img src={qrCodeImg} alt="Google Review QR Code" className="w-48 h-48 object-contain" />
                <div className="flex items-center justify-center gap-2 mt-3 text-sm text-gray-500 font-medium">
                  <SafeIcon icon={FiSmartphone} /> Scan to review
                </div>
              </div>
              <a href={googleLink} target="_blank" rel="noopener noreferrer" className="mt-auto w-full bg-[#4285F4] hover:bg-[#3367D6] text-white font-display text-lg py-4 rounded-xl shadow-md transition-all flex items-center justify-center gap-2 relative z-10 hover:-translate-y-1">
                <SafeIcon icon={FaGoogle} /> Leave Google Review
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-2xl shadow-xl border-t-8 border-brand-green p-8 flex flex-col items-center text-center relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <SafeIcon icon={FiStar} className="text-8xl text-brand-green" />
              </div>
              <h2 className="font-display text-2xl text-brand-navy mb-2 relative z-10">General Review</h2>
              <p className="text-gray-600 mb-8 relative z-10">Prefer to leave a direct review? Share your experience regarding your lessons, booking, and overall satisfaction.</p>
              <div className="flex-1 flex flex-col items-center justify-center relative z-10 w-full mb-8">
                <div className="w-32 h-32 bg-brand-green/10 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={FiStar} className="text-5xl text-brand-green" />
                </div>
                <div className="flex gap-1 text-brand-gold text-2xl">
                  {[...Array(5)].map((_, i) => (<SafeIcon key={i} icon={FiStar} className="fill-current" />))}
                </div>
              </div>
              <a href={tidycalLink} target="_blank" rel="noopener noreferrer" className="mt-auto w-full bg-brand-green hover:bg-[#259b3f] text-white font-display text-lg py-4 rounded-xl shadow-md transition-all flex items-center justify-center gap-2 relative z-10 hover:-translate-y-1">
                Review Us <SafeIcon icon={FiExternalLink} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white border-t border-gray-100">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-brand-navy mb-4">What Our Students Say</h2>
            <div className="w-16 h-1 bg-brand-gold mx-auto rounded-full" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviewImages.map((src, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="aspect-square bg-gray-50 rounded-2xl overflow-hidden shadow-lg border border-gray-200 group relative">
                <img src={src} alt={`Student Review ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-brand-navy/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}